/**
 * @fileoverview Connect the saved matter-notification catalog to focused editing and summary components. `NotificationRuleForm.js` owns drafts/actions; `NotificationRuleChips.js` owns compact presentation. The API owns persisted configuration and worker execution owns evaluation/delivery. Fetching or displaying rules does not run them.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AppButton } from '../shared'
import ToastContext from '../../ToastContext'
import api from '../../api'
import NotificationRuleForm from './NotificationRuleForm'
import NotificationRuleChips from './NotificationRuleChips'
import { AdminCard, AdminEmptyState, AdminPage, AdminSection } from './AdminChrome'

const EMPTY_RULE_FORM = {
    visible: false,
    isEditing: false,
    caseAttributes: [],
    state: '',
    states: [],
    days: 0,
    slackChannel: '',
    oldValue: {},
    id: null,
}



function NotificationRules({ embedded = false }) {
    const [slackChannels, setSlackChannels] = useState([])
    const [rules, setRules] = useState([])
    const [notificationRuleForm, setNotificationRuleForm] = useState(EMPTY_RULE_FORM)
    const showToast = useContext(ToastContext)

    const fetchNotificationRules = useCallback(

    async (showRefreshedToast = false) => {
        try {
            const response = await api.get('/notification_rules')
            const normalized = (response.data || []).map(rule => ({
                SlackChannel: rule.SlackChannelId,
                State: rule.State,
                States: rule.State ? rule.State.split(',').filter(Boolean) : [],
                Days: rule.Days,
                CaseAttributes: rule.CaseAttributes ? rule.CaseAttributes.split(',').filter(Boolean) : [],
                id: rule.id,
            }))
            setRules(normalized)
            if (showRefreshedToast) {
                showToast({ severity: 'info', summary: 'Refreshed', detail: 'Notification rules refreshed' })
            }
        } catch (error) {
            console.error(error)
            if (showRefreshedToast) {
                showToast({ severity: 'error', summary: 'Error', detail: 'Notification rules not refreshed' })
            }
        }
    }, [showToast])

    const fetchSlackChannels = useCallback(

    async () => {
        try {
            const response = await api.get('/automations/slack-channels')
            const channels = (response.data?.allChannels || []).map(channel => ({
                label: channel.name,
                value: channel.id,
            }))
            setSlackChannels(channels)
        } catch (error) {
            console.error(error)
        }
    }, [])

    useEffect(() => {
        fetchSlackChannels()
        fetchNotificationRules(false)
    }, [fetchNotificationRules, fetchSlackChannels])

    const stateRules = useMemo(
        () => rules.filter(rule => !rule.CaseAttributes || rule.CaseAttributes.length === 0),
        [rules]
    )
    const attributeRules = useMemo(
        () => rules.filter(rule => rule.CaseAttributes && rule.CaseAttributes.length > 0),
        [rules]
    )

    const openNotificationRuleForm =

    () => {
        setNotificationRuleForm({
            ...EMPTY_RULE_FORM,
            visible: true,
        })
    }

    const openNotificationRuleFormInEditMode =

    (rule) => {
        setNotificationRuleForm({
            visible: true,
            isEditing: true,
            caseAttributes: rule.CaseAttributes || [],
            state: rule.CaseAttributes?.length ? '' : rule.State,
            states: rule.States || [],
            days: rule.Days ?? 0,
            slackChannel: rule.SlackChannel || '',
            oldValue: rule,
            id: rule.id,
        })
    }

    const Wrapper = embedded ? AdminSection : AdminPage

    return (
        <Wrapper
            title="Notification Rules"
            description="Define reminders for matters that have stalled in a stage or are missing required case attributes."
            actions={(
                <>
                    <AppButton icon="pi pi-plus" variant='secondary' onClick={openNotificationRuleForm} label="Add Rule" />
                    <AppButton icon="pi pi-refresh" variant='info' onClick={() => fetchNotificationRules(true)} label="Refresh" />
                </>
            )}
        >
            <NotificationRuleForm ruleForm={notificationRuleForm} setRuleForm={setNotificationRuleForm} fetchRules={fetchNotificationRules} />

            <AdminCard
                title="State-Based Rules"
                description="These rules alert when a matter remains in one state or substage for too long."
            >
                {stateRules.length > 0 ? (
                    <NotificationRuleChips
                        notificationRules={stateRules}
                        openNotificationRuleFormInEditMode={openNotificationRuleFormInEditMode}
                        slackChannels={slackChannels}
                    />
                ) : (
                    <AdminEmptyState
                        title="No state rules yet"
                        description="Create a state-based rule to remind the team when matters sit in one status too long."
                    />
                )}
            </AdminCard>

            <AdminCard
                title="Missing-Attribute Rules"
                description="These rules alert when a matter stays in selected states while required attributes remain blank."
            >
                {attributeRules.length > 0 ? (
                    <NotificationRuleChips
                        notificationRules={attributeRules}
                        openNotificationRuleFormInEditMode={openNotificationRuleFormInEditMode}
                        slackChannels={slackChannels}
                    />
                ) : (
                    <AdminEmptyState
                        title="No missing-attribute rules yet"
                        description="Create an attribute-based rule to catch missing matter data before it blocks the workflow."
                    />
                )}
            </AdminCard>
        </Wrapper>
    )
}

export default NotificationRules
